<script lang="ts">
import type { TTagProps } from '../TTag';

/**
 * Props for the `TCardButton` component.
 *
 * `TCardButton` is a card-shaped button for choosing an option from a list
 * (e.g. a payment method). It is action-agnostic: it renders a native
 * `<button>` and exposes no custom events — attach a plain `@click` (it falls
 * through to the root) and handle navigation, tracking or selection on the
 * consumer side.
 *
 * Height is content-driven: with only `title` the card compacts naturally.
 */
export interface TCardButtonProps {
  /** Main label. Replace its rendering via the `title` slot when richer content is needed (e.g. a logo image). */
  title: string;
  /** Supporting text under the title. */
  subtitle?: string;
  /**
   * Text for the `TTag` badge. Position depends on `borderless`: floating on
   * the top-right corner on the default bordered card, inline before the
   * trailing icon when borderless.
   */
  tag?: string;
  /**
   * Variant forwarded to the internal `TTag`.
   * @defaultValue 'default'
   */
  tagVariant?: TTagProps['variant'];
  /** Removes the card border. Also decides the `tag` position (see `tag`). */
  borderless?: boolean;
  /** Disables the underlying `<button>`, blocking clicks natively. */
  disabled?: boolean;
}

/**
 * Slots exposed by `TCardButton`. `icon` and `image` are mutually exclusive
 * treatments of the 36×36 media square; when both are provided, `icon` wins.
 */
export interface TCardButtonSlots {
  /** Rendered 16×16, centered inside the media square over its subtle background. Has priority over `image`. */
  icon?: () => unknown;
  /** Fills the whole media square, clipped by its border radius. Ignored when `icon` is provided. */
  image?: () => unknown;
  /** Overrides the `title` prop rendering. */
  title?: () => unknown;
  /** Overrides the internally rendered `TTag`. */
  tag?: () => unknown;
  /** Overrides the default chevron-right icon on the right edge. */
  trailing?: () => unknown;
}
</script>

<script setup lang="ts">
import { TIcon } from '../TIcon';
import { TTag } from '../TTag';

defineOptions({ name: 'TCardButton' });

const {
  title,
  subtitle,
  tag,
  tagVariant = 'default',
  borderless,
  disabled,
} = defineProps<TCardButtonProps>();

defineSlots<TCardButtonSlots>();
</script>

<template>
  <button
    type="button"
    class="t-card-button"
    :data-borderless="borderless || undefined"
    :disabled="disabled"
  >
    <div
      v-if="$slots.icon || $slots.image"
      class="t-card-button__media"
      :data-media="$slots.icon ? 'icon' : 'image'"
    >
      <slot v-if="$slots.icon" name="icon" />
      <slot v-else name="image" />
    </div>

    <!-- TODO: replace the <p> elements with TTypography once it consumes semantic tokens (--color-text-*, --font-*) -->
    <div class="t-card-button__text">
      <slot name="title">
        <p class="t-card-button__title">
          {{ title }}
        </p>
      </slot>
      <p v-if="subtitle" class="t-card-button__subtitle">
        {{ subtitle }}
      </p>
    </div>

    <span
      v-if="tag || $slots.tag"
      class="t-card-button__tag"
    >
      <slot name="tag">
        <TTag size="small" :variant="tagVariant">{{ tag }}</TTag>
      </slot>
    </span>

    <span class="t-card-button__trailing">
      <slot name="trailing">
        <TIcon icon="chevron-right" size="small" color="currentColor" />
      </slot>
    </span>
  </button>
</template>

<style scoped lang="scss">
.t-card-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  width: 100%;
  padding: var(--space-4);
  border: var(--border-width-thin) solid var(--color-border-subtle-default);
  border-radius: var(--radius-surface-default);
  background-color: var(--color-bg-surface-raised-default);
  font-family: var(--font-family-base);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--motion-duration-fast) ease;

  &[data-borderless] {
    border-color: transparent;
  }

  &:hover:not(:disabled) {
    background-color: var(--color-gray-1);
  }

  &:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-ring-focus-default);
    outline-offset: var(--focus-ring-offset);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: var(--opacity-disabled);
  }

  &__media {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--space-9);
    height: var(--space-9);
    overflow: hidden;
    border-radius: var(--radius-control-default);
    background-color: var(--color-bg-surface-default);
    color: var(--color-icon-default-default);

    :deep(svg),
    :deep(img) {
      width: var(--space-4);
      height: var(--space-4);
    }

    &[data-media='image'] {
      background-color: transparent;

      :deep(svg),
      :deep(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: var(--font-b3-font-size);
    line-height: var(--font-b3-line-height);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-default-default);
  }

  &__subtitle {
    margin: 0;
    font-size: var(--font-b4-font-size);
    line-height: var(--font-b4-line-height);
    font-weight: var(--font-weight-regular);
    color: var(--color-text-muted-default);
  }

  &__tag {
    display: flex;
    flex-shrink: 0;
    position: absolute;
    top: 0;
    right: 0;
    overflow: hidden;
    border-radius: 0 calc(var(--radius-surface-default) - var(--border-width-thin)) 0 var(--radius-xs);
  }

  &[data-borderless] &__tag {
    position: static;
    overflow: visible;
    border-radius: 0;
  }

  &__trailing {
    display: flex;
    flex-shrink: 0;
    color: var(--color-icon-muted-default);
  }
}
</style>
